import moment from "moment-timezone";

// Format Date
export const formatToDateString = (date, format = "MM-DD-YYYY") => {
  return moment(date).format(format);
};

// Parse Date
export const parseToDate = (date, format = "MM-DD-YYYY") => {
  return moment(date, format);
};

// Get Current Date
export const getCurrentDateString = (format = "MM-DD-YYYY") => {
  return moment().format(format);
};

// Add Days to Date
export const addDaysToDateString = (date, days) => {
  return moment(date).add(days, "days");
};

export const addYearsToDateString = (date, years) => {
  return moment(date).add(years, "years");
};

// Subtract Days from Date
export const subtractDaysFromDateString = (date, days) => {
  return moment(date).subtract(days, "days").format("MM-DD-YYYY");
};

// Get Difference Between Two Dates
export const calculateDateDifference = (startDate, endDate, unit = "days") => {
  return moment(endDate).diff(moment(startDate), unit);
};

// Check if Date is Valid
export const checkIfDateIsValid = (date) => {
  return moment(date).isValid();
};

// Get Start of Day (Midnight)
export const getStartOfDayString = (date, useUtc = false) => {
  return useUtc
    ? moment(date).utc().startOf("day").format("MM-DD-YYYY HH:mm:ss")
    : moment(date).startOf("day").format("MM-DD-YYYY HH:mm:ss");
};

// Get End of Day (11:59:59 PM)
export const getEndOfDayString = (date, useUtc = false) => {
  return useUtc
    ? moment(date).utc().endOf("day").format("MM-DD-YYYY HH:mm:ss")
    : moment(date).endOf("day").format("MM-DD-YYYY HH:mm:ss");
};

// Check If Date is Before Another Date
export const checkIfDateIsBefore = (date1, date2) => {
  return moment(date1).isBefore(moment(date2));
};

// Check If Date is After Another Date
export const checkIfDateIsAfter = (date1, date2) => {
  return moment(date1).isAfter(moment(date2));
};

// Convert Date to ISO String
export const convertToISOString = (date) => {
  return moment(date).toISOString();
};

// Get Full Month Name
export const getFullMonthName = (date) => {
  return moment(date).format("MMMM");
};

// Get Full Weekday Name
export const getFullWeekdayName = (date) => {
  return moment(date).format("dddd");
};

// Get Relative Time String (e.g., "3 hours ago")
export const getRelativeTimeString = (date) => {
  return moment(date).fromNow();
};

// Get Unix Timestamp
export const getUnixTimestamp = (date) => {
  return moment(date).unix();
};

// Set Time for Date (e.g., 10:30)
export const setSpecificTimeForDate = (date, time) => {
  return moment(date)
    .set({
      hour: time.split(":")[0],
      minute: time.split(":")[1],
    })
    .format("MM-DD-YYYY HH:mm:ss");
};

// Get Start and End Date for a Given Month
export const getStartAndEndOfMonth = (
  month = moment().month(),
  useUtc = false
) => {
  const start = useUtc
    ? moment().month(month).startOf("month").utc().format("MM-DD-YYYY")
    : moment().month(month).startOf("month").format("MM-DD-YYYY");
  const end = useUtc
    ? moment().month(month).endOf("month").utc().format("MM-DD-YYYY")
    : moment().month(month).endOf("month").format("MM-DD-YYYY");
  return { start, end };
};

export const toDate = (date) => {
  return moment(date).toDate();
};

export const adjustDateByYears = (years) => {
  const newDate = new Date();
  newDate.setFullYear(newDate.getFullYear() - years);
  return newDate;
};

// export const getTimeDifference = (start, end, unit = 'all') => {
//   let startDate = start?moment(start):moment();
//   let endDate = moment(end);
//   const duration = moment.duration(endDate.diff(startDate));
//   const formatIfGreaterThanZero = (value, unitLabel) => {
//     if (value > 0) {
//       return `${value} ${unitLabel}`;
//     }
//     return '';
//   };
//   switch (unit) {
//     case 'days':
//       return formatIfGreaterThanZero(duration.days(), 'days');
//     case 'hours':
//       return formatIfGreaterThanZero(duration.hours(), 'hours');
//     case 'minutes':
//       return formatIfGreaterThanZero(duration.minutes(), 'minutes');
//     case 'all':
//     default:
//       const days = formatIfGreaterThanZero(duration.days(), 'days');
//       const hours = formatIfGreaterThanZero(duration.hours(), 'hours');
//       const minutes = formatIfGreaterThanZero(duration.minutes(), 'minutes');
//       return [days, hours, minutes].filter(Boolean).join(', ') || '0 minutes';
//   }
// };
export const getTimeDifference = (start, end, unit = "all") => {
  let startDate = start ? moment(start) : moment();
  let endDate = moment(end).endOf("day");

  const duration = moment.duration(endDate.diff(startDate));

  const totalDays = Math.floor(duration.asDays()); // Total days (not partial)
  const totalHours = Math.floor(duration.asHours()); // Total hours
  const totalMinutes = Math.floor(duration.asMinutes()); // Total minutes

  const formatIfGreaterThanZero = (value, unitLabel) => {
    if (value > 0) {
      return `${value} ${unitLabel}`;
    }
    return "";
  };

  switch (unit) {
    case "days":
      return formatIfGreaterThanZero(totalDays, "days");
    case "hours":
      return formatIfGreaterThanZero(totalHours, "hours");
    case "minutes":
      return formatIfGreaterThanZero(totalMinutes, "minutes");
    case "all":
    default:
      const days = formatIfGreaterThanZero(totalDays, "days");
      const hours = formatIfGreaterThanZero(duration.hours(), "hours"); // Remaining hours
      const minutes = formatIfGreaterThanZero(duration.minutes(), "minutes"); // Remaining minutes
      return [days, hours, minutes].filter(Boolean).join(", ") || `0 ${unit}`;
  }
};

export const formattedDateMMMDDYYYY = (data) => {
  return moment(data).format("MMM DD, YYYY");
};

export const formattedDateDDMMMMYYYY = (date) => {
  return moment(date).format("DD MMMM YYYY");
};

export const formattedDateDDMMMYYYY = (date) => {
  return moment(date).format("DD MMM YYYY");
};

export const formattedDateDMMYYYY = (date) => {
  return moment(date).format("D.MM.YYYY");
};

export const convertToTime = (date) => {
  return moment(date).format("hh:mm A");
};
